//@flow
/**
 * Learner actions
 * @module learner/actions
 */
import ReactGA from 'react-ga'
import { getProposedQuizId } from './lib'
import {
  UPDATE_LEARNER_NOTES,
  UPDATE_LEARNER_NOTES_REQUEST,
  UPDATE_LEARNER_NOTES_SUCCESS,
  UPDATE_LEARNER_NOTES_FAILURE,
  SUBMIT_LEARNER_FORM_REQUEST,
  SUBMIT_LEARNER_FORM_PENDING,
  SUBMIT_LEARNER_FORM_SUCCESS,
  SUBMIT_LEARNER_FORM_FAILURE,
  FINISH_QUIZ_LATER_REQUEST,
  FINISH_QUIZ_LATER_SUCCESS,
  FINISH_QUIZ_LATER_FAILURE,
  SUBMIT_ANSWER_REQUEST,
  SUBMIT_ANSWER_PENDING,
  SUBMIT_ANSWER_SUCCESS,
  SUBMIT_ANSWER_FAILURE,
  RESTORE_LEARNER_REQUEST,
  RESTORE_LEARNER_FAILURE,
  RESTORE_LEARNER_SUCCESS,
  UPDATE_DLRQ_LEARNER,
  COMPLETE_DLRQ_LEARNER_SUCCESS,
  COMPLETE_DLRQ_LEARNER_FAILURE,
  UPDATE_DP_LEARNER,
  COMPLETE_DP_PRESCREENING_SUCCESS,
  DP_PREVIOUS_QUESTION,
  TOGGLE_EDIT_FORM,
  UPDATE_QUESTION_ID,
  CONFIRM_DP_RESET_REQUEST,
  CONFIRM_DP_RESET_FAILURE,
  CONFIRM_DP_RESET_SUCCESS
} from './reducer'

import socket, { whenConnected } from '../../socket'
import { displayInvalidInviteScreen, getClientId } from '../../utils'

import {
  getNextQuestionId,
  canProgressToQuestion
} from '@lrg/lln-quiz-common/lib/learner/lib'
import { preloadQuestions } from '../questions/lib'
import {
  submitQuestion,
  submitLearner,
  addInviteToLocation,
  whenLearnerRegistered,
  submitDlrqResult,
  submitDpResult,
  unsetDpResult,
  getCurrentLevel,
  dpNextQuestionId,
  dpBuildScore,
  submitDpConfirmReset
} from './lib'
import { allTestLevels } from '../../constants'
import { dlrqQuestions, dlrqSuitabilityMatrix } from '../../constants/dlrq'
import { dpQuestions } from '../../constants/dp'

/**
 * Sends request to finish quiz later, triggering an email to a learner
 * @memberof learner/actions
 */
export const finishQuizLater = () => (dispatch: Function) => {
  dispatch({
    type: FINISH_QUIZ_LATER_REQUEST
  })
  socket.emit('learner:completeLater', (err?: Error): void => {
    if (err) {
      return dispatch({
        type: FINISH_QUIZ_LATER_FAILURE,
        payload: err,
        error: true
      })
    }
    dispatch({
      type: FINISH_QUIZ_LATER_SUCCESS
    })
    ReactGA.event({
      category: 'Learner',
      action: 'Finish quiz later'
    })
  })
}

/**
 * Sets a learners notes and triggers a throttled update to the backend
 * @memberof learner/actions
 */
let updateLearnerNotesTimeout
export const setNotes = (notes: string) => (dispatch: Function) => {
  dispatch({
    type: UPDATE_LEARNER_NOTES,
    payload: notes
  })

  clearTimeout(updateLearnerNotesTimeout)
  updateLearnerNotesTimeout = setTimeout(() => {
    dispatch({
      type: UPDATE_LEARNER_NOTES_REQUEST
    })
    socket.emit('learner:notes', notes, (err?: Error): void => {
      if (err) {
        return dispatch({
          type: UPDATE_LEARNER_NOTES_FAILURE,
          payload: err,
          error: true
        })
      }
      dispatch({
        type: UPDATE_LEARNER_NOTES_SUCCESS,
        payload: notes
      })
      ReactGA.event({
        category: 'Learner',
        action: 'Updated notes'
      })
    })
  }, 3000)
}

/**
 * Submits the learners form, updating a learner if registered or creating if not
 * @memberof learner/actions
 */
export const submitLearnerForm =
  (inputs: { [key: string]: { value: string, skipped: boolean } }) =>
  (dispatch: Function, getState: Function) => {
    dispatch({
      type: SUBMIT_LEARNER_FORM_REQUEST
    })

    const {
      client,
      learner: { testLevel, id: learnerId, assessmentVersion }
    } = getState()
    const {
      learnerId: learnerIdInput,
      firstname,
      lastname,
      email,
      hasDeviceAccess,
      courses,
      jobs,
      ...customInputs
    } = inputs
    const learnerDetails = {}
    learnerDetails.learnerId = {
      value: learnerIdInput.value,
      skipped: learnerIdInput.skipped
    }
    learnerDetails.firstname = {
      value: firstname.value
    }
    learnerDetails.lastname = {
      value: lastname.value
    }
    learnerDetails.email = {
      value: email.value
    }
    if(courses && courses.value) {
      learnerDetails.courses = {
        value: courses.value,
      };
    }
    if(jobs && jobs.value) {
      learnerDetails.jobs = {
        value: jobs.value,
      };
    }
    if (hasDeviceAccess && hasDeviceAccess.value) {
      learnerDetails.hasDeviceAccess = { value: hasDeviceAccess.value }
    }
    
    learnerDetails.custom = Array.from(Object.entries(customInputs))
      .filter(([, val = {}]) => val != null && typeof val === 'object')
      .map(([_id, val = {}]) => {
        const { value, skipped } = val
        return { _id, value, skipped }
      })

    if (learnerId) {
      learnerDetails._id = learnerId
    }

    learnerDetails.assessmentVersion = assessmentVersion;

    const proposedQuizId = getProposedQuizId()
    // only set quiz id if it is valid and learner doesn't already have a quizId
    if (
      testLevel === '' &&
      proposedQuizId != null &&
      allTestLevels.includes(proposedQuizId)
    ) {
      learnerDetails.quizId = proposedQuizId
    } else {
      // default to current testLevel, defaulting to client default, and defaulting to acsf3
      learnerDetails.quizId =
        testLevel || client.getIn(['preferences', 'defaultQuizLevel'], 'acsf3')
    }

    var dispatches = [
      {
        type: SUBMIT_LEARNER_FORM_PENDING,
        payload: learnerDetails
      }
    ]

    if (learnerDetails.quizId === 'dlrq') {
      // we don't want to preload questions for the DLRQ quiz and initial question is set
      learnerDetails.initialQuestionRequest = 's0-q1'
      learnerDetails.state = { currentQuestion: 's0-q1', complete: 'false' }
    } else if (learnerDetails.quizId === 'dp') {
      // Digital proficiency questions
      learnerDetails.initialQuestionRequest = 'psq1'
      learnerDetails.state = {
        currentQuestion: 'psq1',
        complete: 'false',
        prescreeningComplete: false
      }
    } else {
      // traditional quiz the questions are randomised
      learnerDetails.initialQuestionRequest = getNextQuestionId(
        learnerDetails.quizId,
        []
      )
      // and we want to preload the question and resources, not required for dlrq
      dispatches.push(preloadQuestions)
    }

    dispatch(dispatches)

    // schedule learner sumission and asume success
    // NOTES: This is probably what is causing the issue where learner answers end up on different quizzes
    whenConnected(async immediate => {
      try {
        const learner = await submitLearner(learnerDetails)

        addInviteToLocation(learner.inviteCode)

        // avoid setting question if quiz has already begun
        if (immediate && learner.state.currentQuestion === null) {
          learner.state.currentQuestion = getNextQuestionId(
            learner.testLevel,
            learner.answers
          )
        }

        dispatch({
          type: SUBMIT_LEARNER_FORM_SUCCESS,
          payload: learner
        })

        ReactGA.set({ userId: `${getClientId()}:${learner.inviteCode}` })
        ReactGA.event({
          category: 'Learner',
          action: 'Submitted Learner Form'
        })
      } catch (err) {
        dispatch({
          type: SUBMIT_LEARNER_FORM_FAILURE,
          payload: err,
          error: true
        })
      }
    })
  }

/**
 * Determines the next question id and schedules a question submission.
 * @memberof learner/actions
 */
export function submitAnswer (
  questionId: string,
  answer: { answer: RawLearnerAnswer, correct: boolean },
  startTime: number
): Function {
  return async (dispatch: Function, getState: Function) => {
    const { learner, questions } = getState()
    const submittingAnswer = learner.get('submittingAnswer', false)
    if (submittingAnswer) return
    dispatch({
      type: SUBMIT_ANSWER_REQUEST,
      payload: {
        questionId,
        answer,
        startTime,
        endTime: Date.now()
      }
    })
    // if any requested questions can be progressed to then request that question
    const answers = [
      ...learner.answers.toJS(),
      {
        questionId,
        answer
      }
    ]
    // request the next question from already requested questions, falling back to
    // questions in the preload path, finally defaulting to quiz finished
    const nextQuestionRequest =
      questions.requested.find(id =>
        canProgressToQuestion(learner.testLevel, id, answers)
      ) ||
      questions.preloadPath.find(id =>
        canProgressToQuestion(learner.testLevel, id, answers)
      ) ||
      'false'
    const questionSubmission = {
      answer: {
        questionId,
        answer: answer.answer,
        correct: answer.correct,
        startTime,
        submitTime: Date.now()
      },
      nextQuestionRequest
    }

    dispatch({
      type: SUBMIT_ANSWER_PENDING,
      payload: questionSubmission
    })

    whenConnected(async () => {
      try {
        await whenLearnerRegistered()
        const response = await submitQuestion(questionSubmission)
        dispatch({
          type: SUBMIT_ANSWER_SUCCESS,
          payload: {
            response,
            questionSubmission
          }
        })
        ReactGA.event({
          category: 'Learner',
          action: 'Submitted Answer',
          label: questionId
        })
      } catch (err) {
        // if already submitted ignore and move on to next question
        if (/answer already submitted/.test(err)) {
          dispatch({
            type: SUBMIT_ANSWER_SUCCESS,
            payload: {
              questionSubmission
            }
          })
        } else {
          return dispatch({
            type: SUBMIT_ANSWER_FAILURE,
            payload: err,
            error: true
          })
        }
      }
    })
  }
}

/**
 * Submits an answer for DLRQ questionnaire.  This is a special quiz type which
 * does not submit results to server every question, but instead submits all answers at the end
 * There is no "answer" processing, answers to current question determine the next question
 * @memberof learner/actions
 */
export function submitAnswerDlrq (
  questionId: string,
  selectedOptionIds: Array<string>
) {
  return async (dispatch: Function, getState: Function) => {
    const { learner } = getState()
    const question = dlrqQuestions[questionId]
    const selectedOptions = question.options.filter(
      x => selectedOptionIds.indexOf(x.id) >= 0
    )
    const answer = { questionId,
      selectedIds: selectedOptionIds,
      questionText: question.text,
      selectedAnswers: selectedOptions.map(x => x.text)
    }


    // Calculate the new score
    let { dlrqScore, dlrqWorkings } = learner
    const capability = calcScore(learner, 'capability', [
      answer,
      ...learner.dlrqResponses
    ])
    dlrqScore.capability = capability.score
    dlrqWorkings.capability = capability.workings

    const access = calcScore(learner, 'access', [
      answer,
      ...learner.dlrqResponses
    ])
    dlrqScore.access = access.score
    dlrqWorkings.access = access.workings

    if (selectedOptions[0].baseline) dlrqScore.baseline = selectedOptions[0].baseline
    if (selectedOptions[0].accessBaseline) dlrqScore.accessBaseline = selectedOptions[0].accessBaseline
    if (selectedOptions[0].focusAreaBaseline) dlrqScore.focusAreaBaseline = selectedOptions[0].focusAreaBaseline
    for (let i = 0; i < 5; i++) {
      const faResult = calcFocusScore(learner, i, [
        answer,
        ...learner.dlrqResponses
      ]);
      dlrqScore.focusAreas[i] = faResult.score;
      dlrqWorkings.focusAreas[i] = faResult.workings;
    }

    let nextQuestionId = question.next || selectedOptions[0].next
    if (!nextQuestionId) {
      // NOTE: Conditions are only based on the Capability score at the moment,
      const condition = question.conditionalnext || selectedOptions[0].conditionalnext
      nextQuestionId =
        dlrqScore.capability >= condition.capability
          ? condition.next
          : condition.else
    }



    if (nextQuestionId !== 'finish') {
      // move to next question
      dispatch({
        type: UPDATE_DLRQ_LEARNER,
        payload: { answer, nextQuestionId, dlrqScore, dlrqWorkings }
      })
    } else {
      // build and submit the result
      const result = buildDlrqResult(dlrqScore)
      try {
        await submitDlrqResult(result, learner.dlrqResponses)
        dispatch({
          type: COMPLETE_DLRQ_LEARNER_SUCCESS,
          payload: {
            result
          }
        })
        ReactGA.event({
          category: 'Learner',
          action: 'Questionaire Completed'
        })
      } catch (error) {
        dispatch({
          type: COMPLETE_DLRQ_LEARNER_FAILURE,
          payload: {
            result
          }
        })
      }
    }
  }
}

/**
 * Submits the DLRQ quiz results to the server
 */
function buildDlrqResult (score) {
  const suitabilityResult = dlrqSuitabilityMatrix[score.access][score.capability];
  return {
    capability: score.capability,
    access: score.access,
    suitabilityResult,
    focusArea: {
      digital: score.focusAreas[0],
      communication: score.focusAreas[1],
      content: score.focusAreas[2],
      protection: score.focusAreas[3],
      technical: score.focusAreas[4]
    }
  }
}

/**
 *
 */
function calcScore (learner, item, answers) {
  let total = 0,
    inputs = 0,
    workings = [] // workings just displayed for Dave during testing to validate calculations

  answers.forEach(answer => {
    const question = dlrqQuestions[answer.questionId]
    const selectedOptions = question.options.filter(
      x => answer.selectedIds.indexOf(x.id) >= 0
    )
    total += selectedOptions.reduce((total, x) => total + x[item], 0)
    inputs += selectedOptions.reduce((used, x) => used + (x[item] ? 1 : 0), 0)

    workings.push(...selectedOptions.filter(x => x[item] !== 0).map(x => x[item]))
  })

  const baseline = item === 'access' ? learner.dlrqScore.accessBaseline : learner.dlrqScore.baseline
  let score = Math.round(
    (baseline + total) / (inputs + 1) + baseline
  )
  score = Math.min(8, Math.max(0, score)) // make sure score is between 0 and 8

  return { score, workings }
}

/**
 * Focus areas are calculated slightly differently
 * - we don't add baseline to the averaged result
 * - and we divide by 3 if there are less than 3 adjustments
 */
function calcFocusScore (learner, index, answers) {
  let total = 0,
    inputs = 0,
    workings = [] // workings just displayed for Dave during testing to validate calculations

  answers.forEach(answer => {
    const question = dlrqQuestions[answer.questionId]
    const selectedOptions = question.options.filter(
      x => answer.selectedIds.indexOf(x.id) >= 0
    )
    total += selectedOptions.reduce(
      (total, x) => total + (x.focusarea ? x.focusarea[index] : 0),
      0
    )
    inputs += selectedOptions.reduce(
      (used, x) => used + (x.focusarea && x.focusarea[index] ? 1 : 0),
      0
    )

    workings.push(
      ...selectedOptions
        .filter(x => x.focusarea && x.focusarea[index] !== 0)
        .map(x => x.focusarea[index])
    )
  })

  const score = Math.round(
    (learner.dlrqScore.focusAreaBaseline + total) / Math.max(inputs, 3)
  )
  return { score, workings }
}

/**
 * Restores a learners state and registers them on the server
 * @memberof learner/actions
 */
export function restoreLearner (invite: string): Function {
  return dispatch => {
    dispatch({
      type: RESTORE_LEARNER_REQUEST
    })
    socket.emit(
      'learner:invite',
      {
        invite: invite
      },
      (err, response) => {
        if (err) {
          if (err === 'Learner already registered on server.') return
          if (err === 'Invalid invite.') {
            return displayInvalidInviteScreen(invite)
          }
          return dispatch({
            type: RESTORE_LEARNER_FAILURE,
            payload: err,
            error: true
          })
        }
        const { learner } = response
        // eslint-disable-next-line no-console
        console.log('learner restored', learner)
        if (learner.state.currentQuestion === null) {
          // if no next question if provided by the server, or not initialised then apply one
          learner.state.currentQuestion = getNextQuestionId(
            learner.testLevel,
            learner.answers
          )
        }
        dispatch([
          {
            type: RESTORE_LEARNER_SUCCESS,
            payload: learner || false
          },
          preloadQuestions
        ])
        ReactGA.set({ userId: `${getClientId()}:${invite}` })
        ReactGA.event({
          category: 'Learner',
          action: 'Restored learner'
        })
      }
    )
  }
}

/**
 * Submits an answer for Digital Proficiency.
 * @memberof learner/actions
 */
export function submitAnswerDp (
  questionId: string,
  selectedOptionIds: Array<string>,
  completePercentage: Number = 0
) {
  const question = dpQuestions[questionId]
  const selectedOptions = question.options.filter(
    x => selectedOptionIds.indexOf(x.id) >= 0
  )
  return async (dispatch: Function, getState: Function) => {
    let nextQuestionId = question.next
    const { learner } = getState()
    let previousQuestionId
    let {
      dpPrescreeningResponse,
      prescreeningComplete,
      dpPrescreeningResult,
      dpMainResponse,
      currentQuestionId
    } = learner
    let answer = {}
    
    // for pre-screening
    if (prescreeningComplete === false) {
        answer = {
          [question.id] : {
            selectedIds: selectedOptionIds,
            questionText: question.text,
            selectedAnswers: selectedOptions.map(x => x.text)
          }
        }
        // CALCULATE PRE-SCREENING
        let dpPrescreeningScore = {}
        const allAnswers = {...dpPrescreeningResponse, ...answer}
      
        Object.keys(allAnswers).forEach(questionId => {
     
          const response = allAnswers[questionId]
   
          const selectedAnswers =
            dpQuestions[questionId]
              .options
              .filter(x => response.selectedIds.indexOf(x.id) >= 0)
          
          // build prescreening levels
          selectedAnswers.forEach(selectedAnswers => {
            Object.keys(selectedAnswers.category).forEach(category => {
              const answerLevel = selectedAnswers.category[category]
              category = category.replace('.', '_')
              if (typeof dpPrescreeningScore[category] === 'undefined') {
                dpPrescreeningScore[category] = answerLevel;
              } else if (dpPrescreeningScore[category] < answerLevel) {
                dpPrescreeningScore[category] = answerLevel;
              }
            })
          })
        });
        
        let prescreeningComplete = false
        if (nextQuestionId === "finish") {
          prescreeningComplete = true
          // DISPLAY COMPLETE MESSAGE
          dispatch({
            type: COMPLETE_DP_PRESCREENING_SUCCESS,
            payload: { dpPrescreeningScore }
          })
          nextQuestionId = dpNextQuestionId(
              dpPrescreeningScore,
              0,
              '1_1',
              []
            ).nextQuestionId || 0

        }

        // SAVE RESULT TO DB
        previousQuestionId = Object.keys(answer)[0]
        await submitDpResult(
          dpPrescreeningScore,
          allAnswers,
          {
            currentQuestionId: nextQuestionId,
            prescreening: true,
            prescreeningComplete,
            previousQuestionId: previousQuestionId,
            completePercentage
          }
        )

    } else {
      // current question category
      let currentCategory = "1_1"
      if (question.category) {
        currentCategory = question.category.replace(".", "_")
      }

      // since sometimes we are updating the question id if there is an issue for the next question
      currentQuestionId = questionId;
      dispatch({
        type: UPDATE_QUESTION_ID,
        payload: {
          currentQuestionId
        }
      })

      // current question level
      const currentLevel = getCurrentLevel(dpPrescreeningResult, currentCategory, currentQuestionId)

      // get total score from points
      let score = 0;
      question.options.forEach(option => {
        if (selectedOptionIds.indexOf(option.id) >= 0) {
          score += option.score // selected option
        } else {
          if (option.unselected > 0) {
            score += option.unselected // unselected option
          }
        }
      })

      // if question type of pick many, get the average
      let isCorrect = false
      if (question.questionType === 'PickMany') {
        const average = score / question.options.length
        isCorrect = average >= currentLevel ? true: false;
      } else {
        isCorrect = score >= currentLevel ? true: false;
      }
      answer = {
        [currentCategory] : {
          [question.id] : {
            questionId: question.id,
            selectedIds: selectedOptionIds,
            questionText: question.instructions,
            selectedAnswers: selectedOptions.map(x => x.text),
            level: currentLevel,
            correct: isCorrect
          }
        }
      }
      const index = Object.keys(answer)[0]
      const qData = Object.values(answer)[0]
      
      let allAnswers = dpMainResponse
      if (dpMainResponse.length === 0) {
        allAnswers = answer
      } else {
        if (typeof allAnswers[index] === 'undefined') {
          allAnswers[index] = {[Object.keys(qData)[0]] : null}
        }
        allAnswers[index][Object.keys(qData)[0]]  = Object.values(qData)[0]
      }

      const nextQuestion = dpNextQuestionId(
        dpPrescreeningResult,
        currentQuestionId,
        currentCategory,
        allAnswers
      )

      // set category
      nextQuestionId = nextQuestion.nextQuestionId

      // additional validation to validate if the next question is correct or not
      if (nextQuestionId !== "finish" && !Array.isArray(dpMainResponse)) {
          const mainResponseCategory = dpMainResponse[nextQuestion.category];
          /**
           * Check if there is no answer yet to category and we will the last digit of generated
           * All question should start from 1 if no answer yet to category
           **/ 
          if (typeof mainResponseCategory === "undefined" && nextQuestionId.slice(-1) !== "1") {
            // update the last char from not 1 to 1
            nextQuestionId = nextQuestionId.slice(0, -1) + "1";
          }
      }

      let result = {}
      previousQuestionId = Object.keys(qData)[0]

      const finalScore = (nextQuestionId === "finish" ? dpBuildScore(allAnswers, dpPrescreeningResult) : null)

      // save answer
      await submitDpResult(
        result,
        allAnswers,
        {
          currentQuestionId: nextQuestionId,
          prescreening: false,
          category: nextQuestion.category,
          finalScore: finalScore,
          previousQuestionId: previousQuestionId,
          completePercentage
        }
      )

      if (nextQuestionId === "finish") {
        dispatch({
          type: COMPLETE_DLRQ_LEARNER_SUCCESS
        })
      }
    }
    
    dispatch({
      type: UPDATE_DP_LEARNER,
      payload: {
        answer: answer,
        nextQuestionId: nextQuestionId,
        prescreeningComplete: prescreeningComplete
      }
    })

  }
}
/**
 * Shows the previous question
 * @memberof learner/actions
 */
export const showPreviousQuestion = (completePercentage = 0) =>
  async (dispatch: Function, getState: Function) => {
    const { learner } = getState()
    let {
      dpPrescreeningResponse,
      dpMainResponse,
    } = learner
    
    let previousQuestionId = null
    let category = null
    let answered = 1
    let prescreeningComplete = false
    
    if (dpMainResponse !== undefined && Object.keys(dpMainResponse).length > 0) {
      prescreeningComplete = true
      category = Object.keys(dpMainResponse).pop()
      let categoryData = dpMainResponse[category]
      answered = Object.keys(categoryData).length
      previousQuestionId = categoryData[Object.keys(categoryData).pop()].questionId
    } else {
      previousQuestionId = Object.keys(dpPrescreeningResponse).pop()
    }

    dispatch({
      type: DP_PREVIOUS_QUESTION,
      payload: {
        questionId: previousQuestionId,
        prescreeningComplete
      }
    });
    await unsetDpResult({
      prescreeningComplete,
      previousQuestionId,
      category,
      answered,
      completePercentage
    })
  }
  export const toggleEditForm = () => (dispatch: Function) => {
    dispatch({
      type: TOGGLE_EDIT_FORM
    })
};

/**
 * Confirm Proceed Reset for Digital Proficiency.
 * DigComp2.2 will reset answer (partial answer)
 * Reset and start answer DigComp3.0
 * @memberof learner/actions
 */
export const confirmResetAnswer = () =>
  (dispatch: Function, getState: Function) => {
    dispatch({
      type: CONFIRM_DP_RESET_REQUEST
    })

    const { learner } = getState()
    const { invite } = learner

    // schedule learner sumission and asume success
    // NOTES: This is probably what is causing the issue where learner answers end up on different quizzes
    whenConnected(async immediate => {
      try {
        const learner = await submitDpConfirmReset(invite)
        dispatch({
          type: CONFIRM_DP_RESET_SUCCESS,
          payload: learner
        })

      } catch (err) {
        dispatch({
          type: CONFIRM_DP_RESET_FAILURE,
          payload: err,
          error: true
        })
      }
    })
  }


// WEBPACK FOOTER //
// ./src/state/learner/actions.js